import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import ErrorModal from "../Security/ErrorModal";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { Endpoints } from "../../Global/Endpoints";
import { toast ,ToastContainer} from 'react-toastify';
import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc =
  `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const WINPaySupportServiceDocument = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [serviceTypeOptions, setServiceTypeOptions] = useState([]);
  const [selectedServiceType, setSelectedServiceType] = useState("");
  const [isServiceTypeListOpen, setIsServiceTypeListOpen] = useState(true);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmButtons, setShowConfirmButtons] = useState(false);
  const [fileToDeleteIndex, setFileToDeleteIndex] = useState(null);
  const userInfo = useSelector((state) => state.userData);
  const fileInputRef = useRef(null);
  const token = localStorage.getItem("AccessToken");
  const contract = useSelector((state) => state.userData);
  const memberID = contract.userData.MemberID;
  const [documentType, setDocumentType] = useState("");
  const [loading, setLoading] = useState(false);
  const [isItemsLoading, setIsItemsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const ssoData = useSelector((state) => state.sso.ssoUserData);
  const isHighmarkPortal = ssoData?.Portal === "Highmark";
  const [isDocsUploading, setIsDocUploading] = useState(false);

  const getPageCount = (file) => {
        return new Promise(async (resolve, reject) => {
            const bytes = await file.arrayBuffer()
            
            const loadingTask = pdfjsLib.getDocument({
                data: bytes,
                disableWorker: true});
             try {
                const pdf = await loadingTask.promise;
                const pages = pdf.numPages;
                resolve(pages);
             } catch (error) {
                reject(error);
            }
        });
    };

  useEffect(() => {
    const fetchServiceTypes = async () => {
      setIsItemsLoading(true);
      try {
        const payload = {
          _Params: JSON.stringify({
            MemberID: memberID,
            Contract: contract.userData.ContractIdentified,
            DeviceType: isHighmarkPortal ? "HighmarkPortal" : "PatientPortal",
            EmailID: contract.userData.EmailID,
          }),
        };
    
        const response = await axios.post(Endpoints.SupportingDocServices, payload, {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${token}`,
          },
        });
    
        // Map the response to dropdown options
        const serviceTypeOptions = response.data.map((item) => ({
          label: item.Name,
          value: item.Value ?? item.Name,
          dateRange: item.DateRange || "",
        }));
    
        setServiceTypeOptions(serviceTypeOptions);
      } catch (error) {
        console.error("Error fetching service types:", error);
        setError("Failed to load service types. Please try again later.");
      } finally {
        setIsItemsLoading(false);
      }
    };
    

    fetchServiceTypes();
  }, [contract, memberID, token]);

  const validateForm = () => {
    const errors = {};
    if (!selectedServiceType) errors.serviceType = t('servicetypereq');
    if (uploadedFiles.length === 0) errors.files = t('atleastonepage');
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFileChange = (event) => {
    const files = event.target.files;
    const validExtensions = ["pdf", "tiff", "jpeg", "jpg", "bmp", "png"];

    let duplicateFound = false;

    // Check for duplicates with already uploaded files
    for (const file of files) {
      const isDuplicate = uploadedFiles.some(
        f => f.name === file.name && f.size === file.size
      );
      if (isDuplicate) {
        duplicateFound = true;
        break;
      }
    }

    // Check for duplicates within the new files themselves
    const fileSignatures = new Set();
    for (const file of files) {
      const signature = `${file.name}_${file.size}`;
      if (fileSignatures.has(signature)) {
        duplicateFound = true;
        break;
      }
      fileSignatures.add(signature);
    }

    if (duplicateFound) {
      toast.error("Duplicate file(s) detected. Please select unique files only.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    if ((files.length + uploadedFiles.length) > 4) {
      toast.error("Maximum 4 files are allowed");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    
    Array.from(files).forEach(file => {
      if (validExtensions.includes(file.name.split(".").pop().toLowerCase()) && file.size < 2 * 1024 * 1024 && !duplicateFound) {
        setUploadedFiles((prevFiles) => [...prevFiles, file]);
        setFormErrors((prevErrors) => ({ ...prevErrors, files: "" }));
      } else {
        setModalMessage(t("invalidfiletype"));
        setShowModal(true);
      }
      if (file.size > 5 * 1024 * 1024) {
        setModalMessage(`The ${file.name} is too large (over 5MB). Please upload a smaller file.`);
        setShowModal(true);
      }
    });
  
    // Always clear the file input value to allow re-upload
    if (fileInputRef.current) {
      fileInputRef.current.value = ""; // Reset file input for invalid or valid uploads
    }
  };

  const handleServiceTypeChange = (option) => {
    setSelectedServiceType(option);
    setIsServiceTypeListOpen(false);
    setFormErrors((prevErrors) => ({ ...prevErrors, serviceType: "" })); // Clear service type error
  };

  const handleDeleteFile = (index) => {
    setModalMessage(t('deletefile'));
    setShowConfirmButtons(true);
    setFileToDeleteIndex(index);
    setShowDeleteModal(true);
  };

  const confirmDeleteFile = () => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== fileToDeleteIndex));
    setShowDeleteModal(false);
    setFileToDeleteIndex(null);
  };

  const handleSubmit = async () => {
    if (!validateForm()) return; // Prevent submission if validation fails

    setIsSubmitting(true);
    setLoading(true);
    try {
      let fileID = 1;
      const payloads = [];

      for (const file of uploadedFiles) {
        let noOfPages = 1;

        if (file.type === "application/pdf") {
          noOfPages = await getPageCount(file);
        }

        const fileToBase64 = (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result.split(",")[1]);
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(file);
          });

        const base64Data = await fileToBase64(file);

        const filePayload = {
          FileID: fileID.toString(), // Use the incrementing FileID
          FilePath: "",
          FileName: file.name,
          NoOFPages: noOfPages.toString(),
          DocTypeDesc: "",
          inputBytes: base64Data,
          fileSize: file.size.toString(),
          memberId: contract.userData.MemberID,
          contract: contract.userData.ContractIdentified,
          contractCode: "",
          docTypeDescription: selectedServiceType,
          DeviceType:isHighmarkPortal ? "HighmarkPortal" : "PatientPortal",
          EmailID:contract.userData.EmailID
        };

        payloads.push(filePayload);
        fileID++;
      }
      const totalFiles = payloads.length;

      setLoading(true);
      setIsDocUploading(true);
      const response = await axios.post(
        Endpoints.UploadDocument,
        JSON.stringify(payloads),
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const sendToWinPayload = {
        _Params: JSON.stringify({
          DocumentTypes: "",
        MemberID: userInfo.userData.MemberID,
        Contract: userInfo.userData.ContractIdentified,
        Speciality:"",
        Activity:"",
        DoctypeDesc:"",
        UserName:"MAPP",
        ProviderID:"",
        GroupID:"",
        TotalFiles: totalFiles,
        Documents: response.data.map((file, index) => ({
          NoOFPages: payloads[index].NoOFPages,
          FilePath: file.FilePath,
          FileID: payloads[index].FileID,
          FileName: payloads[index].FileName,
          DocTypeDesc: "",
        })),
        ReimbursementDetails: "",
        TotalAmount: 0.0,
        PhysicianName: null,
        PhysicianTelNo:"",
        PhysicianTaxNo:"",
        ReimbursementType:"",
        ServiceType:selectedServiceType,
        AuthNo:"",
        Email:"",
        DeviceType:isHighmarkPortal ? "HighmarkPortal" : "PatientPortal",
        EmailID:contract.userData.EmailID
      }),
      };

      const sendToWinResponse = await axios.post(Endpoints.SendToWin, sendToWinPayload, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
      });

    if (sendToWinResponse.data?.Result === undefined || Number(sendToWinResponse.data?.Result) === 2) {
        setModalMessage(sendToWinResponse.data?.ResultText || t('successfulmsg'));
        setShowModal(true);
        setSelectedServiceType(""); // Reset dropdown
        setUploadedFiles([]); // Clear uploaded files
      } else {
        setModalMessage(sendToWinResponse.data?.ResultText || t('errormsg'));
        setShowModal(true);
      }
    } catch (error) {
      console.error("Error submitting documents:", error);
      setModalMessage(t('errormsg'));
      setShowModal(true);
    } finally {
      setIsSubmitting(false);
      setLoading(false);
      setIsDocUploading(false);
    }
  };

  if (loading) {
        return (
            <div className="loading-container">
                <div className="loading mb-2"></div>
                    <h2 className="loading-text">
                    {!isDocsUploading ? (
                        t("loading")
                    ) : (
                        <>
                        Your documents are being securely uploaded.
                        <br />
                        Please wait for a moment.
                        </>
                    )}
                    </h2>            
                </div>
            );
    }

  const onBrowse = () => {
    const input = document.getElementById('fileInput');
    if (input) {
        input.value = null; // Always reset so change event fires even for same file
        input.click();
    }
};

  const selectedServiceOption = serviceTypeOptions.find(
    (option) => option.label === selectedServiceType
  );

  return (
    <>
      <ToastContainer />
      <ErrorModal
        show={showModal}
        message={modalMessage}
        isOkay={true}
        onOkay={fileToDeleteIndex !== null ? confirmDeleteFile : () => setShowModal(false)}
      />
       <ErrorModal
        show={showDeleteModal}
        message={modalMessage}
        onClose={()=>setShowDeleteModal(false)}
        onOkay={fileToDeleteIndex !== null ? confirmDeleteFile : () => setShowModal(false)}
        showConfirmButtons={fileToDeleteIndex !== null}
      />

{isHighmarkPortal ? 
            <div className="col-md-12 col-lg-6 mt-4 d-flex inner-page-heading-wrapper">
              <button className="win-back-btn" onClick={() => navigate("/pay-landingpage")}>
                  <span>
                  <i className="bi bi-chevron-left"></i>
                  </span>
              </button>       
              <h2 className="page-heading">Upload supporting document</h2>
            </div>:
            <div className="col-md-12 col-lg-6 mt-4 d-flex inner-page-heading-wrapper">
              <button className="win-back-btn" onClick={() => navigate("/pay-landingpage")}>
                  <span>
                  <i className="bi bi-chevron-left"></i>    
                  {t('Back')}        
                  </span>
              </button>       
              <h2 className="page-heading">Upload supporting document</h2>
            </div>
          }

       <div className="col-md-12 text-center mb-0 mb-md-0">
           {/* {isHighmarkPortal?<b>I would like to pay for a treatment with Fertility and Family Building card</b>:<b>I would like to pay for a treatment with WINCard</b>} */}
        </div>
      <div className="row mt-0 mt-md-0 pe-0 align-items-start">
        <div className="col-md-7 pe-0">
          <div className="mb-3 row flex-sm-row flex-column">
            <div class="col-lg-8 col-md-12 col-sm-12"><label className="form-label win-form-label mandatory mt-2 supporting-service-label">{t('dropdownsupport')}</label></div>
            <div class="col-lg-10 col-md-12 col-sm-12">
              <div className="supporting-service-options" role="radiogroup" aria-label={t('dropdownsupport')}>
                {isItemsLoading ? (
                  <div className="supporting-service-loading" aria-live="polite" aria-label={t("loading")}>
                    <div className="supporting-service-spinner" />
                  </div>
                ) : selectedServiceOption && !isServiceTypeListOpen ? (
                  <button
                    type="button"
                    className="supporting-service-selected-summary"
                    onClick={() => setIsServiceTypeListOpen(true)}
                    aria-expanded={isServiceTypeListOpen}
                  >
                    <span className="supporting-service-selected-summary-header">
                      <span className="supporting-service-option-title">{selectedServiceOption.label}</span>
                      <i className="bi bi-chevron-down supporting-service-selected-summary-icon" aria-hidden="true"></i>
                    </span>
                    {selectedServiceOption.dateRange && (
                      <span className="supporting-service-selected-summary-date">{selectedServiceOption.dateRange}</span>
                    )}
                  </button>
                ) : (
                  serviceTypeOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`supporting-service-option${!option.dateRange ? " supporting-service-option--single-line" : ""}${selectedServiceType === option.label ? " selected" : ""}`}
                      onClick={() => handleServiceTypeChange(option.label)}
                      aria-pressed={selectedServiceType === option.label}
                    >
                      <span className="supporting-service-option-title">{option.label}</span>
                      {option.dateRange && (
                        <span className="supporting-service-option-date">{option.dateRange}</span>
                      )}
                    </button>
                  ))
                )}
              </div>
              {formErrors.serviceType && (
                <div className="form-field-error-message">{formErrors.serviceType}</div>
              )}
              </div>
          </div>
        </div>
        <div className="col-md-5 pe-0 supporting-upload-column">
          <div className="mb-3 supporting-upload-section">
            <div className="col-12 px-0"><label className="form-label win-form-label mandatory supporting-upload-label">{t('uploadfiles')}</label></div>
            <div className="col-12 px-0 supporting-upload-field">
              <div className="custom-file-upload">
                <input
                  type="file"
                  id="fileInput"
                  multiple
                  className="file-input"
                  onChange={handleFileChange}
                  ref={fileInputRef}
                  accept=".pdf, .tiff, .jpeg, .jpg, .bmp, .png"
                />
                <div className="input-display">
                  <input type="text" className="file-text" placeholder={t('choosefile')} readOnly />
                  <button type="button" className="custom-button" onClick={onBrowse}>{t('browse')}</button>
                </div>
              </div>
              <div className="note-gray-bold-14">
                {t('validationsmsg')}
              </div>
              {formErrors.files && <div className="form-field-error-message">{formErrors.files}</div>}
            </div>
          </div>
        </div>
      </div>
      <div className="mt-3">            
      <div className="form-field-error-message">{t('msg')}</div>
      </div>
      <div className="row mt-2 pe-0">
        <div className="col-12 pe-0 winpay-table-wrapper">
          <table className="table">
            <thead className="winpay-table-header">
              <tr>
                <th>{t('filename')}</th>
                <th width="80" className="text-center">{t("view")}</th>
                <th width="80" className="text-center">{t('delete')}</th>
              </tr>
            </thead>
            <tbody>
              {uploadedFiles.map((file, index) => (
                <tr key={index}>
                  <td>{file.name}</td>
                  <td className="text-center">
                    <a
                      href={URL.createObjectURL(file)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="winpay-table-view-icon mx-auto"
                    />
                  </td>
                  <td className="text-center">
                    <button className="winpay-table-trash-icon mx-auto" onClick={() => handleDeleteFile(index)}></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="col-12 text-center mt-4">
        <button
          className="win-btn-primary mobile-view mb-5"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {t('senddocuments')}
        </button>
      </div>
    </>
  );
};

export default WINPaySupportServiceDocument;

